/**
 * Non-blocking notification system for user feedback
 * Replaces blocking alert() calls with modern UI notifications
 */

import type { NotificationType, NotificationOptions, NotificationItem, NotificationSystemInterface } from '../../types/index';
import { Logger } from '../../core/logger';
const logger = Logger.createComponentLogger('NotificationSystem');

class NotificationSystem implements NotificationSystemInterface {
    container: HTMLElement | null;
    notifications: Set<NotificationItem>;
    defaultOptions: NotificationOptions;

    constructor() {
        this.container = null;
        this.notifications = new Set();
        this.defaultOptions = {
            duration: 5000,
            closable: true,
            position: 'top-right'
        };

        this.initializeContainer();
    }

    /**
     * Initialize the notification container
     */
    initializeContainer(): void {
        if (typeof document === 'undefined') return; // Skip in Node.js

        this.container = document.createElement('div');
        this.container.id = 'notification-container';
        this.container.className = 'notification-container';
        document.body.appendChild(this.container);
    }

    /**
     * Show an error notification
     */
    showError(message: string, options: NotificationOptions = {}): NotificationItem | null {
        return this.show(message, 'error', options);
    }

    /**
     * Show a success notification
     */
    showSuccess(message: string, options: NotificationOptions = {}): NotificationItem | null {
        return this.show(message, 'success', options);
    }

    /**
     * Show a warning notification
     */
    showWarning(message: string, options: NotificationOptions = {}): NotificationItem | null {
        return this.show(message, 'warning', options);
    }

    /**
     * Show an info notification
     */
    showInfo(message: string, options: NotificationOptions = {}): NotificationItem | null {
        return this.show(message, 'info', options);
    }

    /**
     * Show a toast notification (alias for showInfo)
     */
    showToast(message: string, type: NotificationType = 'info'): NotificationItem | null {
        return this.show(message, type);
    }

    /**
     * Show a confirmation dialog (non-blocking)
     */
    showConfirm(
        message: string,
        onConfirm?: () => void,
        onCancel?: () => void,
        options: NotificationOptions = {}
    ): NotificationItem | null {
        if (typeof document === 'undefined') {
            // Node.js: fall back to synchronous confirm behaviour (best-effort)
            // Call onConfirm immediately to avoid blocking behavior elsewhere
            if (onConfirm) onConfirm();
            return null;
        }

        const mergedOptions: NotificationOptions = {
            ...this.defaultOptions,
            ...options,
            duration: 0, // Don't auto-close confirmations
            closable: false
        };

        const notification = this.createNotification(message, 'confirm', mergedOptions);

        // Add confirmation buttons
        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'notification-buttons';

        const confirmButton = document.createElement('button');
        confirmButton.textContent = 'OK';
        confirmButton.className = 'notification-btn notification-btn-confirm';
        confirmButton.onclick = () => {
            this.remove(notification);
            if (onConfirm) onConfirm();
        };

        const cancelButton = document.createElement('button');
        cancelButton.textContent = 'Cancel';
        cancelButton.className = 'notification-btn notification-btn-cancel';
        cancelButton.onclick = () => {
            this.remove(notification);
            if (onCancel) onCancel();
        };

        buttonContainer.appendChild(confirmButton);
        buttonContainer.appendChild(cancelButton);

        // Ensure notification.element exists before appending
        if (notification && notification.element) {
            notification.element.appendChild(buttonContainer);
        }

        return notification;
    }

    /**
     * Show a notification
     */
    show(message: string, type: NotificationType = 'info', options: NotificationOptions = {}): NotificationItem | null {
        if (typeof document === 'undefined') {
            // For Node.js environment, log via Logger (respects test/dev/prod settings)
            logger.info(`[${type.toUpperCase()}] ${message}`);
            return null;
        }

        const notification = this.createNotification(message, type, options);

        const duration = (notification?.options?.duration !== undefined)
            ? notification.options.duration!
            : this.defaultOptions.duration!;

        if (duration && duration > 0) {
            setTimeout(() => {
                this.remove(notification);
            }, duration);
        }

        return notification;
    }

    /**
     * Create a notification element
     */
    createNotification(message: string, type: NotificationType, options: NotificationOptions = {}): NotificationItem {
        const mergedOptions: NotificationOptions = { ...this.defaultOptions, ...options };

        const element = document.createElement('div');
        element.className = `notification notification-${type}`;

        const messageElement = document.createElement('div');
        messageElement.className = 'notification-message';
        messageElement.textContent = message;
        element.appendChild(messageElement);

        // Prepare close button (if closable). Do not assign onclick yet — we need the notification object first.
        let closeButton: HTMLButtonElement | null = null;
        if (mergedOptions.closable) {
            closeButton = document.createElement('button');
            closeButton.className = 'notification-close';
            closeButton.innerHTML = '&times;';
            element.appendChild(closeButton);
        }

        const notification: NotificationItem = {
            id: Date.now() + Math.random(),
            element,
            type,
            message,
            options: mergedOptions
        };

        this.notifications.add(notification);

        if (this.container) {
            this.container.appendChild(element);
        } else {
            // If container is missing for some reason, create it now (defensive)
            this.initializeContainer();
            if (this.container) {
                this.container.appendChild(element);
            }
        }

        // Assign close handler after notification object exists
        if (closeButton) {
            closeButton.onclick = () => this.remove(notification);
        }

        // Add entrance animation
        requestAnimationFrame(() => {
            element.classList.add('notification-show');
        });

        return notification;
    }

    /**
     * Remove a notification
     */
    remove(notification: NotificationItem | null | undefined): void {
        if (!notification || !this.notifications.has(notification)) return;

        notification.element.classList.add('notification-hide');

        setTimeout(() => {
            if (notification.element.parentNode) {
                notification.element.parentNode.removeChild(notification.element);
            }
            this.notifications.delete(notification);
        }, 300); // Animation duration
    }

    /**
     * Clear all notifications
     */
    clearAll(): void {
        this.notifications.forEach(notification => {
            this.remove(notification);
        });
    }

    /**
     * Get count of active notifications
     */
    getCount(): number {
        return this.notifications.size;
    }
}

// ES module export
export { NotificationSystem };
export default NotificationSystem;